import React from "react";
import { Box, Divider, Switch, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import SelectCst from "@components/InputCst/SelectCst";
import DataPickerCst from "@components/InputCst/DataPickerCst";
import InputCst from "@components/InputCst/InputCst";
import MultiSelectCst from "@components/InputCst/MultiSelectCst";
import {
  AttributeOption,
  EnrollType,
  EnrollmentConfigState,
  EnrollmentTargetsState,
  ExpirationType,
} from "../types";
import {
  ConfigValidationResult,
  ENROLL_TYPE_OPTIONS,
  EXPIRATION_TYPE_OPTIONS,
  getDisabledEnrollTypes,
  isTopEnrollType,
} from "../config/enrollmentFieldsConfig";
import { EnrollmentTriggerPayload } from "../types";
import SelectedTargetsRecap from "./SelectedTargetsRecap";

interface ConfigurationStepProps {
  trigger: EnrollmentTriggerPayload | null;
  config: EnrollmentConfigState;
  onChange: (next: EnrollmentConfigState) => void;
  attributes: AttributeOption[];
  errors?: ConfigValidationResult["errors"];
  targets?: EnrollmentTargetsState;
  disabledEnrollTypes?: EnrollType[];
  hiddenEnrollTypes?: EnrollType[];
}

const ConfigurationStep: React.FC<ConfigurationStepProps> = ({
  trigger,
  config,
  onChange,
  attributes,
  errors = {},
  targets,
  disabledEnrollTypes: disabledEnrollTypesProp,
  hiddenEnrollTypes,
}) => {
  const { t } = useTranslation("enrollment-modal");
  const theme = useTheme();

  const update = (patch: Partial<EnrollmentConfigState>) =>
    onChange({ ...config, ...patch });

  const disabledEnrollTypes =
    disabledEnrollTypesProp ?? getDisabledEnrollTypes(trigger);
  const enrollTypeOptions = ENROLL_TYPE_OPTIONS.filter(
    (o) => !hiddenEnrollTypes?.includes(o.value),
  ).map((o) => ({
    value: o.value,
    label: String(t(o.labelKey, o.value)),
  }));

  const renderInlineSwitch = (
    label: string,
    checked: boolean,
    onChange: (next: boolean) => void,
    disabled = false,
  ) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "4px 0",
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <Typography
        sx={{
          fontSize: "14px",
          fontWeight: 700,
          color: theme.palette.text.primary,
        }}
      >
        {label}
      </Typography>
      <Switch
        checked={checked}
        disabled={disabled}
        onChange={(_, next) => onChange(next)}
      />
    </Box>
  );

  const isForcedExpirationDisabled =
    !isTopEnrollType(config.enrollType) ||
    config.expirationKind !== "fixed-date";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      {targets && (
        <>
          <SelectedTargetsRecap targets={targets} />
          <Divider />
        </>
      )}

      <SelectCst
        id="enrollment-enrollType"
        label={t("config.enroll-type.label") || "Modalità di iscrizione"}
        value={config.enrollType ?? ""}
        options={enrollTypeOptions}
        optionsToDisable={disabledEnrollTypes}
        onChange={(v: any) => {
          const next = v as EnrollType;
          update({
            enrollType: next,
            ...(isTopEnrollType(next) ? {} : { forcedExpiration: false }),
          });
        }}
        required
        error={!!errors.enrollType}
        errorMessage={errors.enrollType ? t("validation.required") || "" : ""}
      />

      <Divider />

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 700,
              color: theme.palette.text.primary,
            }}
          >
            {t("config.enable-expiration.label") ||
              "Vuoi impostare una scadenza"}
          </Typography>
          <Typography
            sx={{ fontSize: "12px", color: theme.palette.text.primary }}
          >
            {t("config.enable-expiration.helper") ||
              "Puoi definire un limite entro cui completare l'oggetto formativo."}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontSize: "13px" }}>
            {t("common.no") || "No"}
          </Typography>
          <Switch
            checked={config.enableExpiration}
            onChange={(_, next) =>
              update({
                enableExpiration: next,
                ...(next
                  ? { expirationKind: "fixed-date" }
                  : {
                      expirationKind: undefined,
                      expirationType: undefined,
                      expirationDate: undefined,
                      daysToComplete: undefined,
                      forcedExpiration: false,
                    }),
              })
            }
          />
          <Typography sx={{ fontSize: "13px" }}>
            {t("common.yes") || "Sì"}
          </Typography>
        </Box>
      </Box>

      {config.enableExpiration && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {renderInlineSwitch(
            t("config.expiration-kind.options.completion-days-switch") ||
              "Imposta una scadenza calcolata",
            config.expirationKind === "completion-days",
            (next) =>
              update({
                expirationKind: next ? "completion-days" : "fixed-date",
                ...(next
                  ? { expirationDate: undefined, forcedExpiration: false }
                  : { daysToComplete: undefined }),
              }),
          )}

          {renderInlineSwitch(
            t("config.forced-expiration.label") ||
              "Imposta una scadenza forzata",
            !!config.forcedExpiration && !isForcedExpirationDisabled,
            (next) => update({ forcedExpiration: next }),
            isForcedExpirationDisabled,
          )}

          <SelectCst
            id="enrollment-expirationType"
            label={t("config.expiration-type.label") || "Tipo di scadenza"}
            value={config.expirationType ?? ""}
            options={EXPIRATION_TYPE_OPTIONS.map((o) => ({
              value: o.value,
              label: String(t(o.labelKey, o.value)),
            }))}
            onChange={(v: any) =>
              update({ expirationType: v as ExpirationType })
            }
            required
            error={!!errors.expirationType}
            errorMessage={
              errors.expirationType ? t("validation.required") || "" : ""
            }
          />

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 2,
            }}
          >
            <DataPickerCst
              id="enrollment-expirationDate"
              label={t("config.expiration-date.label") || "Data di scadenza"}
              value={config.expirationDate ?? null}
              onChange={(v: any) =>
                update({
                  expirationDate: v
                    ? typeof v === "string"
                      ? v
                      : v?.toISOString?.()
                    : undefined,
                })
              }
              disabled={config.expirationKind !== "fixed-date"}
              required={config.expirationKind === "fixed-date"}
              error={!!errors.expirationDate}
              errorMessage={
                errors.expirationDate ? t("validation.required") || "" : ""
              }
            />

            <InputCst
              id="enrollment-daysToComplete"
              label={
                t("config.days-to-complete.label") ||
                "Tempo di completamento (giorni)"
              }
              type="number"
              value={
                config.daysToComplete === undefined
                  ? ""
                  : String(config.daysToComplete)
              }
              onChange={(e: any) => {
                const raw = e?.target?.value;
                const n = raw === "" ? undefined : Number(raw);
                update({ daysToComplete: n });
              }}
              disabled={config.expirationKind !== "completion-days"}
              required={config.expirationKind === "completion-days"}
              error={!!errors.daysToComplete}
              errorMessage={
                errors.daysToComplete ? t("validation.required") || "" : ""
              }
            />
          </Box>
        </Box>
      )}

      <Divider />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        <Typography sx={{ fontSize: "14px", fontWeight: 700 }}>
          {t("config.attributes.label") || "Associa attributi"}
        </Typography>
        <Typography
          sx={{ fontSize: "12px", color: theme.palette.text.primary, mb: 1 }}
        >
          {t("config.attributes.helper") ||
            "Seleziona gli attributi da associare a questa iscrizione."}
        </Typography>
        <MultiSelectCst
          id="enrollment-attributes"
          label={t("config.attributes.placeholder") || "Attributi"}
          value={attributes.filter((a) =>
            config.attributesIds.includes(a.value),
          )}
          onChange={(next: any) => {
            const items = Array.isArray(next) ? next : [];
            update({
              attributesIds: items
                .map((it: AttributeOption | number) =>
                  typeof it === "number" ? it : it?.value,
                )
                .filter((v): v is number => typeof v === "number"),
            });
          }}
          options={attributes}
        />
      </Box>
    </Box>
  );
};

export default ConfigurationStep;
